import { Extension } from '@tiptap/core'
import type { ResolvedPos, Schema } from '@tiptap/pm/model'
import { Mark } from '@tiptap/pm/model'
import type { Selection } from '@tiptap/pm/state'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

interface VirtualCursorExtensionOptions {
  skipWarning?: string[]
}

export const FakeCursor = Extension.create<VirtualCursorExtensionOptions>({
  name: 'fakeCursor',
  addOptions() {
    return {
      skipWarning: undefined,
    }
  },
  addProseMirrorPlugins() {
    return [
      createVirtualCursor({
        skipWarning: this.options.skipWarning,
        isEditable: this.editor.isEditable,
      }),
    ]
  },
})

export interface VirtualCursorOptions {
  /**
   * An array of ProseMirror mark names that should be ignored when checking the
   * [`inclusive`](https://prosemirror.net/docs/ref/#model.MarkSpec.inclusive)
   * attribute. You can also set this to `gp` to skip the warning altogether.
   */
  skipWarning?: string[] | true
  isEditable?: boolean
}

const debug = false

export function createVirtualCursor(options?: VirtualCursorOptions): Plugin {
  const skipWarning = options?.skipWarning ?? false

  let _cursor: HTMLElement | null =
    typeof document === 'undefined' ? null : document.createElement('div')

  return new Plugin({
    key,
    view: (view) => {
      if (skipWarning !== true) {
        checkInclusive(view.state.schema, skipWarning || [])
      }

      const doc = view.dom.ownerDocument
      _cursor = _cursor || document.createElement('div')
      const cursor = _cursor

      const update = () => {
        updateCursor(view, cursor)
      }

      let observer: ResizeObserver | undefined
      if (window.ResizeObserver) {
        observer = new window.ResizeObserver(() => update())
        observer.observe(view.dom)
      }
      doc.addEventListener('selectionchange', update)

      return {
        update: () => {
          update()
        },
        destroy: () => {
          doc.removeEventListener('selectionchange', update)
          if (observer) {
            observer.unobserve(view.dom)
          }
        },
      }
    },
    props: {
      handleKeyDown: (view, event): boolean => {
        if (debug) {
          console.log('---------------------------------------------')
        }
        const { selection } = view.state

        if (
          event.altKey ||
          event.ctrlKey ||
          event.metaKey ||
          event.shiftKey ||
          event.isComposing ||
          !['ArrowLeft', 'ArrowRight'].includes(event.key) ||
          !isTextSelection(selection) ||
          !selection.empty
        ) {
          if (debug) console.log('EARLY RETURN')
          return false
        }

        const $pos = selection.$head
        const [marksBefore, marksAfter] = getMarksAround($pos)
        const marks = view.state.storedMarks || $pos.marks()

        // Don't move the cursor, only change the stored marks
        if (
          marksBefore &&
          marksAfter &&
          !Mark.sameSet(marksBefore, marksAfter)
        ) {
          if (event.key === 'ArrowLeft' && !Mark.sameSet(marksBefore, marks)) {
            view.dispatch(view.state.tr.setStoredMarks(marksBefore))
            return true
          }

          if (event.key === 'ArrowRight' && !Mark.sameSet(marksAfter, marks)) {
            view.dispatch(view.state.tr.setStoredMarks(marksAfter))

            return true
          }
        }

        // Handle cases for when there are no marks before or after the cursor
        if (marksBefore?.length || marksAfter?.length) {
          // `|Mark` -> |`Mark`
          if (
            !$pos.nodeBefore &&
            marksAfter &&
            Mark.sameSet(marksAfter, marks) &&
            event.key === 'ArrowLeft'
          ) {
            if (debug) {
              console.log('ArrowLeft and no marks before')
            }
            view.dispatch(
              view.state.tr
                .setSelection(TextSelection.create(view.state.doc, $pos.pos))
                .setStoredMarks([])
            )
            return true
          }

          if (
            !$pos.nodeAfter &&
            marksBefore &&
            !Mark.sameSet(marksBefore, marks) &&
            event.key === 'ArrowLeft'
          ) {
            if (debug) {
              console.log('ArrowLeft and marks before')
            }
            view.dispatch(
              view.state.tr
                .setSelection(TextSelection.create(view.state.doc, $pos.pos))
                .setStoredMarks(marksBefore)
            )
            return true
          }

          if (
            !$pos.nodeAfter &&
            marksBefore &&
            Mark.sameSet(marksBefore, marks) &&
            event.key === 'ArrowRight'
          ) {
            if (debug) {
              console.log('ArrowRight and no marks after')
            }
            view.dispatch(
              view.state.tr
                .setSelection(TextSelection.create(view.state.doc, $pos.pos))
                .setStoredMarks([])
            )
            return true
          }
          if (
            !$pos.nodeBefore &&
            marksAfter &&
            !Mark.sameSet(marksAfter, marks) &&
            event.key === 'ArrowRight'
          ) {
            if (debug) {
              console.log('ArrowRight and marks after')
            }
            view.dispatch(
              view.state.tr
                .setSelection(TextSelection.create(view.state.doc, $pos.pos))
                .setStoredMarks(marksAfter)
            )
            return true
          }
        }

        if (debug) {
          // Move the cursor and also change the stored marks
          console.log('TEXT OFFSET', $pos.textOffset)
          console.log('PARENT', $pos.parent)
          console.log('INDEX', $pos.index())
          console.log(
            'NODE SIZE',
            $pos.parent.maybeChild($pos.index() - 1)?.nodeSize
          )
        }

        // Move the cursor and also change the stored marks
        const nodeSize = $pos.parent.maybeChild($pos.index() - 1)?.nodeSize
        if (
          event.key === 'ArrowLeft' &&
          ((nodeSize === 1 && $pos.textOffset === 0) || $pos.textOffset === 1)
        ) {
          view.dispatch(
            view.state.tr
              .setSelection(TextSelection.create(view.state.doc, $pos.pos - 1))
              .setStoredMarks($pos.marks())
          )

          if (debug) console.log('MOVE THE CURSOR AND STORE MARKS LEFT <-')

          return true
        }

        if (event.key === 'ArrowRight' && $pos.textOffset + 1 === nodeSize) {
          view.dispatch(
            view.state.tr
              .setSelection(TextSelection.create(view.state.doc, $pos.pos + 1))
              .setStoredMarks($pos.marks())
          )

          if (debug) console.log('MOVE THE CURSOR AND STORE MARKS ->')
          return true
        }

        return false
      },

      decorations: (state) => {
        if (
          !_cursor ||
          !isTextSelection(state.selection) ||
          !state.selection.empty ||
          // if content is empty, don't show the cursor
          state.doc.nodeSize <= 4 ||
          options?.isEditable === false
        ) {
          return
        }

        return DecorationSet.create(state.doc, [
          Decoration.widget(0, _cursor, {
            key: 'prosemirror-virtual-cursor',
          }),
        ])
      },
    },
  })
}

const key = new PluginKey('prosemirror-virtual-cursor')

function getCursorRect(
  view: EditorView,
  toStart: boolean,
  currentMarks: readonly Mark[] | Mark[] | null | undefined,
  marksAround: [
    readonly Mark[] | Mark[] | null | undefined,
    readonly Mark[] | Mark[] | null | undefined,
  ]
): { left: number; right: number; top: number; bottom: number } | null {
  const $pos = view.state.selection.head
  // check if the nodes are in different lines
  let nodeBeforeRect, nodeAfterRect
  try {
    nodeBeforeRect = view.coordsAtPos($pos - 1)
    nodeAfterRect = view.coordsAtPos($pos + 1)
  } catch (e) {
    return null
  }

  const [_marksBefore, marksAfter] = marksAround
  const diffBetweenNodes = nodeAfterRect.top - nodeBeforeRect.top
  if (diffBetweenNodes > 5) {
    if (
      currentMarks &&
      !!marksAfter?.length &&
      Mark.sameSet(marksAfter, currentMarks)
    ) {
      // get the rect of the next node
      const rect = view.coordsAtPos($pos + 1)
      return { ...rect, left: rect.left - 8, right: rect.right + 8 }
    } else if (
      currentMarks &&
      !!marksAfter?.length &&
      !Mark.sameSet(marksAfter, currentMarks)
    ) {
      // get the rect of the current node
      const rect = view.coordsAtPos($pos + 1)
      return { ...rect, left: rect.left - 8, right: rect.right + 8 }
    } else if (
      currentMarks &&
      !!_marksBefore?.length &&
      Mark.sameSet(_marksBefore, currentMarks)
    ) {
      // get the rect of the previous node
      const rect = view.coordsAtPos($pos - 1)
      return { ...rect, left: rect.left + 8, right: rect.right - 8 }
    } else if (
      currentMarks &&
      !!_marksBefore?.length &&
      !Mark.sameSet(_marksBefore, currentMarks)
    ) {
      // get the rect of the current node
      const rect = view.coordsAtPos($pos - 1)
      return { ...rect, left: rect.left + 8, right: rect.right - 8 }
    }
  }

  const selection = window.getSelection()
  if (!selection || !selection.rangeCount) return null

  const range = selection?.getRangeAt(0)?.cloneRange()
  if (!range) return null

  range.collapse(toStart)
  const rects = range.getClientRects()
  // On Chrome, if the cursor is at the end of the line, there would be two
  // rects. We prefer the last one, which is at the start of the next line.
  const rect = rects?.length ? rects[rects.length - 1] : null
  if (rect?.height) return rect

  return view.coordsAtPos(view.state.selection.head)
}

function getMarksAround($pos: ResolvedPos) {
  const index = $pos.index()
  const after = $pos.parent.maybeChild(index)

  // When inside a text node, just return the text node's marks
  let before = $pos.textOffset ? after : null

  if (!before && index > 0) before = $pos.parent.maybeChild(index - 1)

  return [before?.marks, after?.marks] as const
}

function isTextSelection(selection: Selection): selection is TextSelection {
  return selection && typeof selection === 'object' && '$cursor' in selection
}

function updateCursor(view?: EditorView, cursor?: HTMLElement) {
  if (!view || !view.dom || view.isDestroyed || !cursor) return

  const { state, dom } = view
  const { selection } = state
  if (!isTextSelection(selection)) return

  let className = 'prosemirror-virtual-cursor'

  const $pos = state.selection.$head
  const [marksBefore, marksAfter] = getMarksAround($pos)
  const marks = state.storedMarks || $pos.marks()

  const index = $pos.index()
  const after = $pos.parent.maybeChild(index)

  const cursorRect = getCursorRect(view, !!marksBefore?.length, marks, [
    marksBefore,
    marksAfter,
  ])

  if (!cursorRect) return cursor

  const editorRect = dom.getBoundingClientRect()

  // When inside a text node, just return the text node's marks
  let before = $pos.textOffset ? after : null

  if (!before && index > 0) before = $pos.parent.maybeChild(index - 1)
  if (debug) console.log(marks, marksBefore, marksAfter, '|', before, after)

  const isMarkAfterAndBefore = marksBefore && marksAfter

  if (selection.$cursor && (marksBefore || marksAfter) && marks) {
    if (
      marksBefore?.some((mark) => mark.type.name === 'code') ||
      marksAfter?.some((mark) => mark.type.name === 'code')
    ) {
      className += ' prosemirror-virtual-cursor-code'
    }

    if (isMarkAfterAndBefore && !Mark.sameSet(marksBefore, marksAfter)) {
      if (Mark.sameSet(marksBefore, marks)) {
        if (debug) console.log('1')
        className += ' prosemirror-virtual-cursor-left'
      } else if (Mark.sameSet(marksAfter, marks)) {
        if (debug) console.log('2')
        className += ' prosemirror-virtual-cursor-right'
      }
    } else if (
      !before &&
      marksAfter &&
      marksAfter.length &&
      Mark.sameSet(marksAfter, marks)
    ) {
      if (debug) console.log('3')
      className += ' prosemirror-virtual-cursor-right'
    } else if (
      !before &&
      marksAfter &&
      marksAfter.length &&
      !Mark.sameSet(marksAfter, marks)
    ) {
      if (debug) console.log('4')
      className += ' prosemirror-virtual-cursor-left'
    } else if (
      !after &&
      marksBefore &&
      marksBefore.length &&
      Mark.sameSet(marksBefore, marks)
    ) {
      if (debug) console.log('5')
      className += ' prosemirror-virtual-cursor-left'
    } else if (
      !after &&
      marksBefore &&
      marksBefore.length &&
      !Mark.sameSet(marksBefore, marks)
    ) {
      if (debug) console.log('6')
      className += ' prosemirror-virtual-cursor-right'
    }
  } else {
    className += ' hidden'
  }
  // if there's a mark

  cursor.className = className
  restartAnimation(cursor, 'prosemirror-virtual-cursor-animation')
  cursor.style.height = `${cursorRect.bottom - cursorRect.top}px`
  cursor.style.left = `${cursorRect.left - editorRect.left}px`
  cursor.style.top = `${cursorRect.top - editorRect.top}px`
}

// Restart CSS animation
// https://css-tricks.com/restart-css-animation/
function restartAnimation(element: HTMLElement, className: string) {
  // -> removing the class
  element.classList.remove(className)

  // -> triggering reflow /* The actual magic */
  // eslint-disable-next-line no-void
  void element.offsetWidth

  // -> and re-adding the class
  element.classList.add(className)
}

function checkInclusive(schema: Schema, skipWarning: string[]) {
  for (const [mark, type] of Object.entries(schema.marks)) {
    if (type.spec.inclusive === false && !skipWarning.includes(mark)) {
      console.warn(
        `[prosemirror-virtual-cursor] Virtual cursor does not work well with marks that have inclusive set to false. Please consider removing the inclusive option from the "${mark}" mark or adding it to the "skipWarning" option.`
      )
    }
  }
}
